/**
 * Arthaus Search Module
 * Phase: 3
 * 
 * Extensible search module with adapter pattern for backend swapping.
 * Currently wired to Shopify's default search endpoint.
 * Designed to be replaced with a custom API + AI concierge in the future.
 * 
 * Architecture:
 *   SearchAdapter (interface) → ShopifySearchAdapter (default)
 *                              → ArthausSearchAdapter (future: custom API + AI)
 *   SearchUI (renders results) → uses whichever adapter is registered
 * 
 * Usage:
 *   ArthausSearch.init({ container: '#search-overlay', adapter: 'shopify' });
 *   // Future: ArthausSearch.init({ container: '#search-overlay', adapter: 'arthaus' });
 */

(function() {
  'use strict';

  // ── Adapter Interface ──────────────────────────────────────────────────
  // All adapters must implement: search(query, options) → Promise<SearchResults>
  //
  // SearchResults shape:
  // {
  //   query: string,
  //   products: [{ id, title, handle, url, image, price, artist, type }],
  //   artists: [{ name, url, image }],          // optional — for AI concierge
  //   collections: [{ title, url, image }],      // optional
  //   suggestions: [string],                     // optional — autocomplete
  //   concierge: { message: string, cta: {} },   // optional — AI response
  //   meta: { total: number, page: number }
  // }

  var adapters = {};

  // ── Shopify Default Adapter ────────────────────────────────────────────
  adapters.shopify = {
    name: 'shopify',

    search: function(query, options) {
      if (!query || query.length < 2) {
        return Promise.resolve({ query: query, products: [], suggestions: [], meta: { total: 0 } });
      }

      var limit = (options && options.limit) || 6;
      var types = (options && options.types) || 'product';

      // Use Shopify's predictive search API
      var url = '/search/suggest.json?q=' + encodeURIComponent(query) +
                '&resources[type]=' + types +
                '&resources[limit]=' + limit +
                '&resources[options][unavailable_products]=hide' +
                '&resources[options][fields]=title,vendor,product_type,tag';

      return fetch(url, {
        headers: { 'Accept': 'application/json' }
      })
      .then(function(res) { return res.json(); })
      .then(function(data) {
        var resources = data.resources || {};
        var results = resources.results || {};
        var products = (results.products || []).map(function(p) {
          return {
            id: p.id,
            title: p.title,
            handle: p.handle,
            url: p.url,
            image: p.image || (p.featured_image && p.featured_image.url) || '',
            price: p.price,
            price_formatted: p.price ? ('$' + (parseInt(p.price) / 100).toFixed(0)) : '',
            artist: p.vendor || '',
            type: p.product_type || ''
          };
        });

        return {
          query: query,
          products: products,
          artists: [],           // Shopify adapter doesn't return artist results
          collections: [],       // Shopify adapter doesn't return collection results
          suggestions: [],       // Shopify adapter doesn't return suggestions
          concierge: null,       // No AI concierge in Shopify adapter
          meta: { total: products.length, page: 1 }
        };
      });
    }
  };

  // ── Mock AI Adapter (for Phase 3 Demo) ─────────────────────────────────
  adapters.mock_ai = {
    name: 'mock_ai',

    search: function(query, options) {
      return new Promise(function(resolve) {
        setTimeout(function() {
          resolve({
            query: query,
            products: [
              { id: 1, title: 'Serene Landscape I', handle: 'serene-landscape', url: '/collections/all', image: 'https://images.unsplash.com/photo-1579783900882-c0d3dad7b119?w=400&q=80', price_formatted: '$1,200', artist: 'Elena Rostova' },
              { id: 2, title: 'Earth Tones Abstract', handle: 'earth-tones-abstract', url: '/collections/all', image: 'https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=400&q=80', price_formatted: '$850', artist: 'Julian Vance' },
              { id: 3, title: 'Morning Light', handle: 'morning-light', url: '/collections/all', image: 'https://images.unsplash.com/photo-1580136608260-4eb11f4b24fe?w=400&q=80', price_formatted: '$3,400', artist: 'Sophia Lee' },
              { id: 4, title: 'Wabi-Sabi Texture', handle: 'wabi-sabi-texture', url: '/collections/all', image: 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=400&q=80', price_formatted: '$600', artist: 'Marco Silva' }
            ],
            artists: [
              { name: 'Elena Rostova', url: '/collections/all', image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&q=80' },
              { name: 'Julian Vance', url: '/collections/all', image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&q=80' }
            ],
            collections: [],
            suggestions: [],
            concierge: {
              message: "I've curated a selection of calming, organic pieces that beautifully match '" + query + "'. These focus on natural textures and grounding colors, perfect for anchoring a living space."
            },
            meta: { total: 4, page: 1 }
          });
        }, 800); // 800ms mock delay
      });
    }
  };

  // ── Arthaus Concierge Adapter (Picasso stop-gap service) ───────────────
  // Talks to the FastAPI/LangGraph picasso-concierge service (ams/services/
  // picasso-concierge) which does semantic, graph-backed art discovery over the
  // offline artwork graph. The service response already matches the SearchResults
  // shape, so mapping is near-passthrough.
  //
  // Activate by passing { adapter: 'arthaus', apiBase: '<service-url>' } to
  // ArthausSearch.init (wired from the section schema settings). apiBase is the
  // service origin, e.g. 'https://picasso-concierge-xxxx.run.app'.
  adapters.arthaus = {
    name: 'arthaus',
    apiBase: '',

    search: function(query, options) {
      if (!query || query.length < 2) {
        return Promise.resolve({ query: query, products: [], suggestions: [], meta: { total: 0 } });
      }
      if (!this.apiBase) {
        return Promise.reject(new Error('Arthaus adapter: apiBase not configured'));
      }

      var limit = (options && options.limit) || 12;
      var concierge = !(options && options.concierge === false);
      // fast = skip the Gemini query-expansion (lexical+facet graph retrieval only)
      // for an instant keyword path. Forward-compatible: the service ignores it until
      // it honors the flag; results still come from the graph either way.
      var fast = !!(options && options.fast);
      // Optional per-request retrieval backend (A/B), e.g. 'graphrag' to get the
      // knowledge subgraph + a graph-guided message. Omitted → service default (hybrid).
      var body = { q: query, limit: limit, concierge: concierge, fast: fast };
      if (options && options.backend) body.backend = options.backend;

      return fetch(this.apiBase.replace(/\/$/, '') + '/api/search', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Arthaus-Client': 'storefront'
        },
        body: JSON.stringify(body)
      })
      .then(function(res) {
        if (!res.ok) throw new Error('Concierge search failed: ' + res.status);
        return res.json();
      })
      .then(function(data) {
        // Map API response to the standard SearchResults shape. Build relative,
        // on-domain product URLs from the handle (the service returns absolute
        // shop.myarthaus.com URLs, and a few graph records have no handle at all),
        // and drop any record we can't resolve to a product page.
        var products = (data.products || []).map(function(p) {
          var url = p.handle ? '/products/' + p.handle : (p.url || '');
          return {
            id: p.id, title: p.title, handle: p.handle, url: url,
            image: p.image || '', price_formatted: p.price_formatted || '', artist: p.artist || ''
          };
        }).filter(function(p) { return p.url; });
        return {
          query: data.query || query,
          products: products,
          artists: data.artists || [],
          collections: data.collections || [],
          suggestions: data.suggestions || [],
          concierge: data.concierge || null,  // { message }
          follow_ups: data.follow_ups || [],  // suggested next searches
          knowledge: data.knowledge || null,  // graphrag semantic subgraph (chips)
          intent: data.intent || null,        // explicit lookup: { type, label }
          meta: data.meta || { total: products.length, page: 1 }
        };
      });
    }
  };

  // ── Search UI ──────────────────────────────────────────────────────────
  var SearchUI = {
    container: null,
    input: null,
    resultsEl: null,
    adapter: null,
    debounceTimer: null,
    isOpen: false,
    emptyHtml: '',

    init: function(options) {
      this.adapter = configureAdapter(options);
      this.container = document.querySelector((options && options.container) || '#search-overlay');
      if (!this.container) return;

      this.input = this.container.querySelector('[data-search-input]');
      this.resultsEl = this.container.querySelector('[data-search-results]');
      if (!this.input || !this.resultsEl) return;
      this.emptyHtml = this.resultsEl.innerHTML;

      // Allow rebinding if DOM elements changed
      this._bound = false;

      this.bindEvents();
    },

    bindEvents: function() {
      var self = this;

      // Guard: only bind once
      if (this._bound) return;
      this._bound = true;

      // Input handler with debounce
      this.input.addEventListener('input', function() {
        clearTimeout(self.debounceTimer);
        var query = this.value.trim();

        if (query.length < 2) {
          self.renderEmpty();
          return;
        }

        self.renderLoading();
        self.debounceTimer = setTimeout(function() {
          self.performSearch(query);
        }, 250);
      });

      // Escape to close — listen on document so it fires regardless of focus
      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && self.isOpen) self.close();
      });

      // Close button
      var closeBtn = this.container.querySelector('[data-search-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', function(e) {
          e.stopPropagation();
          self.close();
        });
      }

      // Click backdrop (the overlay itself, outside the inner content) to close
      this.container.addEventListener('click', function(e) {
        // Only close if clicking directly on the container (backdrop), not child elements
        if (e.target === self.container) self.close();
      });
    },

    open: function(seed) {
      this.container.hidden = false;
      this.container.classList.remove('hidden');
      this.container.classList.add('flex');

      // Force a reflow so the transition works
      void this.container.offsetWidth;

      this.container.classList.remove('opacity-0');
      this.container.classList.add('opacity-100');

      this.isOpen = true;
      document.body.style.overflow = 'hidden';
      var self = this;
      setTimeout(function() {
        // Deep-link / concept-card entry: pre-seed and run immediately.
        if (seed) self.searchFor(seed);
        else if (self.input) self.input.focus();
      }, 100);
    },

    // Pre-seed the input and run the search now (no debounce) — used by concept-card
    // deep-links, suggestion chips, and the dedicated concierge page.
    searchFor: function(query, opts) {
      query = (query || '').trim();
      if (!query) return;
      if (this.input) this.input.value = query;
      this.renderLoading();
      this.performSearch(query, opts && opts.limit);
    },

    close: function() {
      this.container.classList.remove('opacity-100');
      this.container.classList.add('opacity-0');
      this.isOpen = false;
      document.body.style.overflow = '';
      if (this.input) this.input.value = '';
      this.renderEmpty();
      
      var self = this;
      setTimeout(function() {
        if (!self.isOpen) {
          self.container.classList.add('hidden');
          self.container.classList.remove('flex');
          self.container.hidden = true;
        }
      }, 500); // match transition duration
    },

    // Two-phase progressive search: stream graph products in fast (no Gemini message),
    // then slot the concierge curator experience in parallel when it arrives.
    performSearch: function(query, limit) {
      if (this.container) this.container.classList.add('has-results');
      this.progressiveInto(this.resultsEl, query, { limit: limit || 6 });
    },

    // Render into ANY element with two regions: a concierge slot (curator message,
    // streams in second) and a products slot (graph results, render first). Reused
    // by the overlay and the dedicated /concierge page. Race-guarded per element.
    progressiveInto: function(el, query, opts) {
      var self = this;
      opts = opts || {};
      var limit = opts.limit || 6;
      var seq = (el.__searchSeq = (el.__searchSeq || 0) + 1);
      this.stopLoaderCycle(el);  // clear any prior staged-loader interval

      // Concierge slot splits into a curator block (staged loader → message + follow-ups,
      // phase 2) above a guide block (knowledge chips, phase 1 — with the fast products).
      el.innerHTML =
        '<div data-concierge-slot class="mb-8">' +
          '<div data-curator>' + this.stagedLoaderHTML() + '</div>' +
          '<div data-guide></div>' +
        '</div>' +
        '<div data-products-slot>' + this.productsLoadingHTML() + '</div>';
      var curatorEl = el.querySelector('[data-curator]');
      var guideEl = el.querySelector('[data-guide]');
      var productsSlot = el.querySelector('[data-products-slot]');
      this.startLoaderCycle(el);  // cycle the "concierge is working" status lines

      // Phase 1 — fast: products + the knowledge chips (graphrag returns the subgraph without
      // the Gemini message). opts.productsBackend (e.g. 'graphrag') keeps grid + chips aligned.
      var p1 = { limit: limit, concierge: false, fast: true };
      if (opts.productsBackend) p1.backend = opts.productsBackend;
      this.adapter.search(query, p1)
        .then(function(r) {
          if (seq !== el.__searchSeq) return;
          productsSlot.innerHTML = self.buildProductsHTML(r);
          var chips = self.buildKnowledgeHTML(r && r.knowledge);
          guideEl.innerHTML = chips;  // chips now, or clear the pending spinner if none
        })
        .catch(function() {
          if (seq !== el.__searchSeq) return;
          guideEl.innerHTML = '';
          productsSlot.innerHTML = '<div class="py-12 text-center"><p class="font-body text-sm text-warm-gray">Something went wrong. Please try again.</p></div>';
        });

      // Phase 2 — the curator layer: graph-guided message + follow-up searches. fast:true skips
      // the Gemini query-expansion (graphrag seeds by vector, so it adds latency, not signal).
      var p2 = { limit: limit, concierge: true, fast: true };
      if (opts.conciergeBackend) p2.backend = opts.conciergeBackend;
      this.adapter.search(query, p2)
        .then(function(r) {
          if (seq !== el.__searchSeq) return;
          self.stopLoaderCycle(el);
          var msg = r && r.concierge && r.concierge.message;
          curatorEl.innerHTML =
            (msg ? self.buildConciergeMessageHTML(msg) : '') +
            self.buildIntentActionHTML(r && r.intent) +
            self.buildFollowupsHTML(r && r.follow_ups);
          // Phase 2 also carries knowledge — fill the guide if phase 1 hadn't yet.
          if (r && r.knowledge && !guideEl.querySelector('[data-search-suggestion]')) {
            guideEl.innerHTML = self.buildKnowledgeHTML(r.knowledge);
          }
        })
        .catch(function() {
          if (seq !== el.__searchSeq) return;
          self.stopLoaderCycle(el);
          curatorEl.innerHTML = '';  // drop the loader; phase-1 products + chips remain
        });
    },

    // ── Staged "concierge is working" loader ───────────────────────────────
    loaderStages: [
      'Reading the palette and light…',
      'Tracing the subject and mood…',
      'Walking the art knowledge graph…',
      'Finding kindred works…',
      'Composing your curation…'
    ],

    stagedLoaderHTML: function() {
      return '<div class="p-5 bg-parchment rounded-lg border border-border">' +
          '<div class="flex items-start gap-3">' +
            '<div class="w-8 h-8 rounded-full bg-bronze/10 flex items-center justify-center shrink-0 mt-0.5">' +
              '<svg class="concierge-sparkle text-bronze" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>' +
            '</div>' +
            '<div class="flex-1 pt-0.5">' +
              '<p class="font-body text-sm text-charcoal transition-opacity duration-300" data-loader-line>' + this.escapeHtml(this.loaderStages[0]) + '</p>' +
              '<div class="concierge-loader-track mt-3 h-0.5 bg-border/50 rounded-full"></div>' +
            '</div>' +
          '</div>' +
        '</div>';
    },

    startLoaderCycle: function(el) {
      var self = this, i = 0;
      el.__loaderTimer = setInterval(function() {
        i = Math.min(i + 1, self.loaderStages.length - 1);
        var line = el.querySelector('[data-loader-line]');
        if (line) {
          line.style.opacity = '0';
          setTimeout(function() { var l = el.querySelector('[data-loader-line]'); if (l) { l.textContent = self.loaderStages[i]; l.style.opacity = '1'; } }, 200);
        }
        // Mid-curation, the skeletons "warm up" — faster sweep + bronze tint (anticipation).
        if (i >= 2) {
          var sk = el.querySelectorAll('.concierge-skeleton');
          for (var s = 0; s < sk.length; s++) sk[s].classList.add('is-warming');
        }
        if (i >= self.loaderStages.length - 1) self.stopLoaderCycle(el);  // hold on the last line
      }, 1500);
    },

    stopLoaderCycle: function(el) {
      if (el && el.__loaderTimer) { clearInterval(el.__loaderTimer); el.__loaderTimer = null; }
    },

    // Suggested next searches (full NL queries) — a guided "where to go next" rail.
    buildFollowupsHTML: function(followUps) {
      var items = (followUps || []).filter(function(s) { return s && s.trim(); }).slice(0, 4);
      if (!items.length) return '';
      var chips = '';
      for (var i = 0; i < items.length; i++) {
        chips += '<button type="button" data-search-suggestion="' + this.escapeHtml(items[i]) + '" ' +
          'class="font-body text-sm text-charcoal bg-parchment border border-border rounded-lg px-3.5 py-2 ' +
          'hover:border-bronze hover:text-bronze transition-colors cursor-pointer text-left">' +
          this.escapeHtml(items[i]) + '</button>';
      }
      return '<div class="mt-5 concierge-reveal">' +
        '<p class="font-mono text-xs text-mute uppercase tracking-wider mb-2.5">Continue exploring</p>' +
        '<div class="flex flex-wrap gap-2">' + chips + '</div>' +
      '</div>';
    },

    // Shopify-style handle (matches `| handleize` used for the PDP artist link).
    handleize: function(s) {
      return String(s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    },

    // Quick action for an explicit ARTIST lookup → jump straight to their collection page.
    buildIntentActionHTML: function(intent) {
      if (!intent || intent.type !== 'artist' || !intent.label) return '';
      var handle = this.handleize(intent.label);
      if (!handle) return '';
      return '<div class="mt-4 concierge-reveal">' +
        '<a href="/collections/' + handle + '" ' +
          'class="flex items-center justify-center gap-2.5 w-full font-body text-base bg-charcoal text-white rounded-md px-5 py-4 ' +
          'hover:bg-bronze transition-colors">' +
          '<span>View all works by ' + this.escapeHtml(intent.label) + '</span>' +
          '<span aria-hidden="true">&rarr;</span>' +
        '</a>' +
      '</div>';
    },

    // Render the graphrag knowledge subgraph as guided-exploration chips. Each chip re-runs
    // a search (via the [data-search-suggestion] handler the overlay/page already wire).
    buildKnowledgeHTML: function(knowledge) {
      if (!knowledge) return '';
      var groups = [
        { label: 'Themes', items: knowledge.themes },
        { label: 'Concepts', items: knowledge.concepts },
        { label: 'Artists', items: knowledge.artists }
      ];
      var chips = '', seen = {};
      for (var gi = 0; gi < groups.length; gi++) {
        var items = (groups[gi].items || []).slice(0, 5);
        for (var i = 0; i < items.length; i++) {
          var name = items[i] && items[i].name;
          if (!name || seen[name.toLowerCase()]) continue;
          seen[name.toLowerCase()] = 1;
          chips += '<button type="button" data-search-suggestion="' + this.escapeHtml(name) + '" ' +
            'class="font-body text-xs text-charcoal bg-white border border-border rounded-full px-3 py-1.5 ' +
            'hover:border-bronze hover:text-bronze transition-colors cursor-pointer">' +
            this.escapeHtml(name) + '</button>';
        }
      }
      if (!chips) return '';
      return '<div class="mt-4 concierge-reveal">' +
        '<p class="font-mono text-xs text-mute uppercase tracking-wider mb-2.5">Explore this aesthetic</p>' +
        '<div class="flex flex-wrap gap-2">' + chips + '</div>' +
      '</div>';
    },

    // Subtle "concierge is curating" indicator shown above products until phase 2 lands.
    conciergePendingHTML: function() {
      return '<div class="flex items-center gap-2.5 text-warm-gray py-1">' +
        '<div class="w-4 h-4 border-2 border-bronze/30 border-t-bronze rounded-full animate-spin"></div>' +
        '<span class="font-body text-xs animate-pulse">The concierge is curating your selection…</span>' +
      '</div>';
    },

    productsLoadingHTML: function() {
      var tiles = '';
      for (var i = 0; i < 6; i++) {
        tiles += '<div class="aspect-[3/4] rounded-md concierge-skeleton' + (i >= 4 ? ' hidden md:block' : '') +
          '" style="animation-delay:' + (i * 140) + 'ms"></div>';
      }
      return '<div class="grid grid-cols-2 md:grid-cols-3 gap-4">' + tiles + '</div>';
    },

    renderLoading: function() {
      if (this.container) this.container.classList.add('has-results');
      this.resultsEl.innerHTML =
        '<div class="py-8">' +
          '<div class="flex items-center gap-3 mb-8">' +
            '<div class="w-5 h-5 border-2 border-bronze/30 border-t-bronze rounded-full animate-spin"></div>' +
            '<p class="font-body text-sm text-warm-gray animate-pulse">The AI Concierge is analyzing your request...</p>' +
          '</div>' +
          '<div class="mb-8 p-5 rounded-lg border border-border/50 bg-parchment/50 animate-pulse h-24"></div>' +
          '<div class="grid grid-cols-2 md:grid-cols-4 gap-4">' +
            '<div class="aspect-[3/4] bg-border/20 rounded-md animate-pulse"></div>' +
            '<div class="aspect-[3/4] bg-border/20 rounded-md animate-pulse"></div>' +
            '<div class="aspect-[3/4] bg-border/20 rounded-md animate-pulse"></div>' +
            '<div class="aspect-[3/4] bg-border/20 rounded-md animate-pulse hidden md:block"></div>' +
          '</div>' +
        '</div>';
    },

    renderEmpty: function() {
      if (this.resultsEl) { this.stopLoaderCycle(this.resultsEl); this.resultsEl.innerHTML = this.emptyHtml || ''; }
      if (this.container) this.container.classList.remove('has-results');
    },

    renderError: function() {
      this.resultsEl.innerHTML =
        '<div class="py-12 text-center">' +
          '<p class="font-body text-sm text-warm-gray">Something went wrong. Please try again.</p>' +
        '</div>';
    },

    renderResults: function(results) {
      if (this.container) this.container.classList.add('has-results');
      this.resultsEl.innerHTML = this.buildResultsHTML(results);
    },

    // Composition (one-shot): concierge message + products. Kept for back-compat.
    buildResultsHTML: function(results) {
      var msg = results && results.concierge && results.concierge.message;
      return (msg ? this.buildConciergeMessageHTML(msg) : '') + this.buildProductsHTML(results);
    },

    // The curator message card (bronze sparkle) — streamed in as phase 2.
    buildConciergeMessageHTML: function(message) {
      if (!message) return '';
      return '<div class="p-5 bg-parchment rounded-lg border border-border concierge-reveal">' +
          '<div class="flex items-start gap-3">' +
            '<div class="w-8 h-8 rounded-full bg-bronze/10 flex items-center justify-center shrink-0 mt-0.5">' +
              '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" class="text-bronze"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>' +
            '</div>' +
            '<div><p class="font-body text-sm text-charcoal leading-relaxed">' + this.escapeHtml(message) + '</p></div>' +
          '</div>' +
        '</div>';
    },

    // The product grid (+ artists + view-all) — rendered first from the fast graph call.
    buildProductsHTML: function(results) {
      if (!results || !results.products || results.products.length === 0) {
        return '<div class="py-12 text-center">' +
            '<p class="font-display text-xl text-charcoal font-light mb-2">No results for "' + this.escapeHtml(results && results.query) + '"</p>' +
            '<p class="font-body text-sm text-warm-gray">Try a different search term</p>' +
          '</div>';
      }

      var html = '';
      // ── Product results ───────────────────────────────────────
      // Explicit lookups (lexical router) label the section by what was matched.
      var heading = 'Artworks';
      if (results.intent && results.intent.label) {
        heading = results.intent.type === 'artist' ? ('Works by ' + results.intent.label) : results.intent.label;
      }
      html += '<div class="mb-6"><h3 class="font-body text-xs text-mute uppercase tracking-wider mb-4">' + this.escapeHtml(heading) + '</h3>';
      html += '<div class="grid grid-cols-2 md:grid-cols-3 gap-4">';

      for (var i = 0; i < results.products.length; i++) {
        var p = results.products[i];
        // Staggered fade+rise reveal — the cascade is the "artwork reveal" moment.
        var delay = Math.min(i * 55, 700);
        html +=
          '<a href="' + p.url + '" class="group block concierge-reveal" style="animation-delay:' + delay + 'ms">' +
            '<div class="aspect-[3/4] rounded-md overflow-hidden bg-parchment mb-2.5 relative">' +
              (p.image ? '<img src="' + p.image + '" alt="' + this.escapeHtml(p.title) + '" class="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" loading="lazy" width="400" height="533">' : '') +
            '</div>' +
            '<h4 class="font-display text-sm text-charcoal font-light truncate">' + this.escapeHtml(p.title) + '</h4>' +
            (p.artist ? '<p class="font-body text-xs text-warm-gray truncate">' + this.escapeHtml(p.artist) + '</p>' : '') +
            (p.price_formatted ? '<p class="font-body text-xs text-charcoal mt-1">' + p.price_formatted + '</p>' : '') +
          '</a>';
      }

      html += '</div></div>';

      // ── Artist results (future) ───────────────────────────────
      if (results.artists && results.artists.length > 0) {
        html += '<div class="mb-6 pt-4 border-t border-border/50"><h3 class="font-body text-xs text-mute uppercase tracking-wider mb-4">Artists</h3>';
        html += '<div class="space-y-3">';
        for (var j = 0; j < results.artists.length; j++) {
          var a = results.artists[j];
          html +=
            '<a href="' + a.url + '" class="flex items-center gap-3 group">' +
              (a.image ? '<img src="' + a.image + '" class="w-10 h-10 rounded-full object-cover" loading="lazy" width="40" height="40" alt="' + this.escapeHtml(a.name) + '">' : '<div class="w-10 h-10 rounded-full bg-parchment"></div>') +
              '<span class="font-body text-sm text-charcoal group-hover:text-bronze transition-colors">' + this.escapeHtml(a.name) + '</span>' +
            '</a>';
        }
        html += '</div></div>';
      }

      // ── View all link ─────────────────────────────────────────
      html +=
        '<div class="pt-4 border-t border-border/50">' +
          '<a href="/search?q=' + encodeURIComponent(results.query) + '" class="font-body text-sm text-bronze hover:underline">View all results for "' + this.escapeHtml(results.query) + '" →</a>' +
        '</div>';

      return html;
    },

    escapeHtml: function(str) {
      if (!str) return '';
      return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }
  };

  // ── Adapter selection (module-level, shared by the overlay singleton and the
  //    stateless query() path used by the dedicated /concierge page) ─────────
  var activeAdapter = null;
  function configureAdapter(options) {
    var a = adapters[(options && options.adapter) || 'mock_ai'];
    // Configure the Arthaus concierge adapter's service origin, if provided.
    if (options && options.apiBase && adapters.arthaus) adapters.arthaus.apiBase = options.apiBase;
    // Fall back to Shopify predictive search if the concierge service URL is unset,
    // so the box (and concept deep-links) still work rather than erroring.
    if (a === adapters.arthaus && !adapters.arthaus.apiBase) a = adapters.shopify;
    activeAdapter = a;
    return a;
  }

  // ── Public API ─────────────────────────────────────────────────────────
  window.ArthausSearch = {
    init: function(options) { SearchUI.init(options); },
    open: function(seed) { SearchUI.open(seed); },
    close: function() { SearchUI.close(); },
    searchFor: function(query, opts) { SearchUI.searchFor(query, opts); },
    registerAdapter: function(name, adapter) { adapters[name] = adapter; },
    getAdapter: function(name) { return adapters[name]; },
    // For the dedicated /concierge page: configure the adapter without binding the
    // overlay UI, run a query, and render results into an arbitrary element.
    configure: function(options) { return configureAdapter(options); },
    query: function(q, opts) { return (activeAdapter || adapters.shopify).search(q, opts || { limit: 12, concierge: true }); },
    renderResultsHTML: function(results) { return SearchUI.buildResultsHTML(results); },
    // Two-phase progressive render (fast graph products → concierge message in parallel)
    // into any element. Used by the overlay and the dedicated /concierge page.
    progressiveInto: function(el, query, opts) { return SearchUI.progressiveInto(el, query, opts); }
  };
})();
